import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  Shield, CheckCircle2, X, ArrowRight, Building2, Globe,
  FileCheck, Users, TrendingUp, Clock, FileText, Lock,
  MapPin, Phone, Mail, Handshake, Scale, Eye, Database
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

const TrustCompliancePage = () => {
  const { t } = useTranslation();

  // What we coordinate
  const whatWeCoordinate = [
    'Air freight coordination through selected partners',
    'Ocean freight coordination via trusted carriers',
    'Road freight coordination across Europe',
    'Multimodal logistics coordination',
    'Customs coordination through external agents',
    'Warehousing coordination with third-party operators',
    'Time-critical logistics coordination',
    'Dangerous goods coordination through qualified partners',
    'Cargo insurance contact facilitation through licensed providers',
    'Partner coordination desk services'
  ];

  // What we do NOT do
  const whatWeDoNot = [
    'Own or operate trucks, aircraft, vessels, or warehouses',
    'Physically handle, load, or transport cargo',
    'Act as customs declarant or licensed customs broker',
    'Provide insurance or act as an insurance intermediary',
    'Issue carrier documents as the carrier',
    'Operate as an NVOCC (Non-Vessel Operating Common Carrier)',
    'Claim FENEX/TLN, FIATA, IATA, or AEO accreditation',
    'Make guarantees about transit times (third-party dependent)',
    'Provide legal or regulatory compliance advice'
  ];

  // Operating model principles
  const operatingModel = [
    {
      icon: Users,
      title: 'Single Point of Contact',
      description: 'One coordinator manages your logistics request from start to finish.'
    },
    {
      icon: FileText,
      title: 'Written Confirmations',
      description: 'All instructions confirmed in writing before partner coordination begins.'
    },
    {
      icon: Scale,
      title: 'Supplier Comparison',
      description: 'Where feasible, we compare minimum two supplier options for transparency.'
    },
    {
      icon: Clock,
      title: 'Milestone Updates',
      description: 'Regular status updates and documented tracking throughout the process.'
    },
    {
      icon: Database,
      title: 'Digital Shipment Files',
      description: 'Complete documentation maintained for every coordinated shipment.'
    },
    {
      icon: Eye,
      title: 'Clear Accountability',
      description: 'Transparent about roles, responsibilities, and what is handled by partners.'
    }
  ];

  // Accreditation roadmap
  const accreditationRoadmap = [
    { name: 'FENEX/TLN Membership', status: 'planned', note: 'Dutch Forwarding Association - planned as business matures' },
    { name: 'FIATA Membership', status: 'planned', note: 'International Federation - requires FENEX membership first' },
    { name: 'IATA Accreditation', status: 'not-applicable', note: 'Not applicable - we coordinate through IATA partners' },
    { name: 'AEO Certification', status: 'planned', note: 'Authorized Economic Operator - future objective' },
    { name: 'ISO 9001', status: 'planned', note: 'Quality Management - planned as operations scale' },
    { name: 'Professional Liability Insurance', status: 'in-progress', note: 'Being finalized - contact us for status' }
  ];

  return (
    <div className="min-h-screen bg-offwhite" data-testid="trust-compliance-page">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-navy-900 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 50 Q 25 25, 50 50 T 100 50' stroke='%23C7A24E' stroke-width='0.5' fill='none'/%3E%3C/svg%3E")`,
            backgroundSize: '200px 100px'
          }} />
        </div>
        
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl mx-auto text-center"
          >
            <span className="inline-block text-gold font-space text-sm tracking-widest uppercase pb-2 border-b-2 border-gold/60 mb-6">
              Trust & Compliance
            </span>
            <h1 className="font-space text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Transparency Builds Trust
            </h1>
            <p className="text-platinum-light text-lg md:text-xl leading-relaxed">
              We believe trust comes from clarity about who we are, what we do, and how we operate. This page provides complete transparency about Elementary Cargo&apos;s role, operating model, and compliance status.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Legal Identity Section */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-gold text-sm font-semibold tracking-wider uppercase mb-4 block">
                Legal Identity
              </span>
              <h2 className="font-space text-3xl md:text-4xl font-bold text-navy-900 mb-6">
                Company Information
              </h2>
              
              <Card className="bg-navy-900 border-none">
                <CardContent className="p-8">
                  <div className="space-y-4">
                    <div className="flex justify-between items-center py-3 border-b border-royal/20">
                      <span className="text-platinum-light flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-gold" />
                        Company Name
                      </span>
                      <span className="font-semibold text-white">Elementary Cargo</span>
                    </div>
                    <div className="flex justify-between items-center py-3 border-b border-royal/20">
                      <span className="text-platinum-light flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-gold" />
                        Location
                      </span>
                      <span className="font-semibold text-white">The Netherlands</span>
                    </div>
                    <div className="flex justify-between items-center py-3 border-b border-royal/20">
                      <span className="text-platinum-light flex items-center gap-2">
                        <FileCheck className="w-4 h-4 text-gold" />
                        KvK Number
                      </span>
                      <span className="font-semibold text-white">42041124</span>
                    </div>
                    <div className="flex justify-between items-center py-3 border-b border-royal/20">
                      <span className="text-platinum-light flex items-center gap-2">
                        <Globe className="w-4 h-4 text-gold" />
                        EORI Number
                      </span>
                      <span className="font-semibold text-white">NL3785680245</span>
                    </div>
                    <div className="flex justify-between items-center py-3">
                      <span className="text-platinum-light flex items-center gap-2">
                        <FileText className="w-4 h-4 text-gold" />
                        VAT Number
                      </span>
                      <span className="font-semibold text-white">NL005449408B56</span>
                    </div>
                  </div>

                  <div className="mt-8 pt-6 border-t border-royal/20">
                    <h4 className="font-semibold text-gold mb-4">Contact</h4>
                    <div className="space-y-3">
                      <a href="mailto:info@elementarycargo.com" className="flex items-center gap-3 text-platinum-light hover:text-gold transition-colors">
                        <Mail className="w-5 h-5" />
                        info@elementarycargo.com
                      </a>
                      <a href="tel:+31625381824" className="flex items-center gap-3 text-platinum-light hover:text-gold transition-colors">
                        <Phone className="w-5 h-5" />
                        +31 62 538 1824
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-gold text-sm font-semibold tracking-wider uppercase mb-4 block">
                Our Role
              </span>
              <h2 className="font-space text-3xl md:text-4xl font-bold text-navy-900 mb-6">
                What Elementary Cargo Is
              </h2>
              
              <div className="space-y-6 text-slate-600 text-lg leading-relaxed">
                <p>
                  Elementary Cargo is an independent logistics coordination company acting as a 4PL-style freight coordinator. We serve as a single point of coordination for international freight and logistics operations.
                </p>
                <p>
                  Our role is to coordinate, facilitate, and manage communication between clients and qualified third-party logistics providers. We do not own transport assets or physically handle cargo.
                </p>
                <div className="bg-sky-50 border border-sky-200 rounded-xl p-6">
                  <div className="flex items-start gap-3">
                    <TrendingUp className="w-6 h-6 text-sky-600 flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-semibold text-sky-800 mb-2">Young Company Status</h4>
                      <p className="text-sky-700 text-base">
                        Elementary Cargo is a young, independent company building its operations and partner network. We are transparent about our current status and work only with qualified external partners for execution.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What We Do / Don't Do */}
      <section className="py-20 md:py-28 bg-offwhite">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-gold text-sm font-semibold tracking-wider uppercase mb-4 block">
              Scope Definition
            </span>
            <h2 className="font-space text-3xl md:text-4xl lg:text-5xl font-bold text-navy-900 mb-4">
              Clear Role Boundaries
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              We are explicit about what we coordinate and what we do not claim to do.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* What We Coordinate */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Card className="h-full border-2 border-green-200 bg-green-50/50">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center">
                      <CheckCircle2 className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="font-space text-xl font-bold text-navy-900">What We Coordinate</h3>
                  </div>
                  <ul className="space-y-3">
                    {whatWeCoordinate.map((item) => (
                      <li key={item} className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-700 text-sm">{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </motion.div>

            {/* What We Don't Do */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Card className="h-full border-2 border-slate-200 bg-slate-50/50">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-full bg-slate-400 flex items-center justify-center">
                      <X className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="font-space text-xl font-bold text-navy-900">What We Do NOT Do</h3>
                  </div>
                  <ul className="space-y-3">
                    {whatWeDoNot.map((item) => (
                      <li key={item} className="flex items-start gap-3">
                        <X className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-700 text-sm">{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Operating Model */}
      <section className="py-20 md:py-28 bg-navy-900">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-gold text-sm font-semibold tracking-wider uppercase mb-4 block">
              How We Work
            </span>
            <h2 className="font-space text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
              Our Operating Model
            </h2>
            <p className="text-lg text-platinum-light max-w-2xl mx-auto">
              Structured processes and documented execution through selected partners.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {operatingModel.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="h-full bg-navy-800/50 border border-royal/20 hover:border-gold/30 transition-colors">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-lg bg-gold/20 flex items-center justify-center flex-shrink-0">
                        <item.icon className="w-6 h-6 text-gold" />
                      </div>
                      <div>
                        <h3 className="font-space text-lg font-bold text-white mb-2">{item.title}</h3>
                        <p className="text-platinum-light text-sm leading-relaxed">{item.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Specific Statements */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-gold text-sm font-semibold tracking-wider uppercase mb-4 block">
              Compliance Statements
            </span>
            <h2 className="font-space text-3xl md:text-4xl lg:text-5xl font-bold text-navy-900 mb-4">
              Important Clarifications
            </h2>
          </motion.div>

          <div className="max-w-4xl mx-auto space-y-6">
            {/* Customs Statement */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <Card className="border border-platinum-light">
                <CardContent className="p-6">
                  <h3 className="font-space text-lg font-bold text-navy-900 mb-3">Customs Coordination</h3>
                  <p className="text-slate-600">
                    Elementary Cargo coordinates communication with external customs agents where customs support is required. Elementary Cargo does not act as the customs declarant. All customs declarations are filed by licensed customs agents engaged through our coordination.
                  </p>
                </CardContent>
              </Card>
            </motion.div>

            {/* Insurance Statement */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <Card className="border border-platinum-light">
                <CardContent className="p-6">
                  <h3 className="font-space text-lg font-bold text-navy-900 mb-3">Insurance Facilitation</h3>
                  <p className="text-slate-600">
                    Elementary Cargo does not broker insurance or provide insurance advice. Where cargo insurance is needed, we can facilitate contact with licensed insurance providers. Coverage decisions and policies remain between the client and the insurance provider.
                  </p>
                </CardContent>
              </Card>
            </motion.div>

            {/* Dangerous Goods Statement */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <Card className="border border-platinum-light">
                <CardContent className="p-6">
                  <h3 className="font-space text-lg font-bold text-navy-900 mb-3">Dangerous Goods Coordination</h3>
                  <p className="text-slate-600">
                    Elementary Cargo can coordinate dangerous goods logistics through qualified partners, subject to classification, documentation, carrier acceptance, and applicable regulations. Elementary Cargo does not physically handle dangerous goods.
                  </p>
                </CardContent>
              </Card>
            </motion.div>

            {/* Data Protection Statement */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <Card className="border border-platinum-light">
                <CardContent className="p-6">
                  <h3 className="font-space text-lg font-bold text-navy-900 mb-3">Data Protection</h3>
                  <p className="text-slate-600">
                    Elementary Cargo processes personal and shipment data in accordance with GDPR/AVG requirements. International logistics coordination may require sharing shipment information with selected logistics partners, customs agents, warehousing operators, carriers, or other necessary parties. See our Privacy Policy for full details.
                  </p>
                </CardContent>
              </Card>
            </motion.div>

            {/* Liability Statement */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <Card className="border border-platinum-light">
                <CardContent className="p-6">
                  <h3 className="font-space text-lg font-bold text-navy-900 mb-3">Liability & Terms</h3>
                  <p className="text-slate-600">
                    Elementary Cargo acts as logistics coordinator and intermediary. Third-party execution means carrier, warehouse, and customs agent terms may apply. Liability limitations are subject to applicable Dutch law and valid incorporation of conditions. See our Terms and Conditions for full details.
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Accreditation Roadmap */}
      <section className="py-20 md:py-28 bg-offwhite">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-gold text-sm font-semibold tracking-wider uppercase mb-4 block">
              Future Development
            </span>
            <h2 className="font-space text-3xl md:text-4xl lg:text-5xl font-bold text-navy-900 mb-4">
              Accreditation Roadmap
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Elementary Cargo is not currently FENEX/TLN, FIATA, IATA, AEO, NVOCC, customs broker, or AFM accredited. We aim to pursue relevant memberships and certifications as the business matures.
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto">
            <div className="space-y-4">
              {accreditationRoadmap.map((item, index) => (
                <motion.div
                  key={item.name}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="border border-platinum-light">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between flex-wrap gap-4">
                        <div className="flex items-center gap-4">
                          <div className={`w-3 h-3 rounded-full ${
                            item.status === 'in-progress' ? 'bg-amber-500' :
                            item.status === 'planned' ? 'bg-blue-500' :
                            'bg-slate-300'
                          }`} />
                          <div>
                            <h3 className="font-semibold text-navy-900">{item.name}</h3>
                            <p className="text-sm text-slate-600">{item.note}</p>
                          </div>
                        </div>
                        <span className={`text-xs font-medium px-3 py-1 rounded-full ${
                          item.status === 'in-progress' ? 'bg-amber-100 text-amber-700' :
                          item.status === 'planned' ? 'bg-blue-100 text-blue-700' :
                          'bg-slate-100 text-slate-600'
                        }`}>
                          {item.status === 'in-progress' ? 'In Progress' :
                           item.status === 'planned' ? 'Planned' : 'Partner-Based'}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-28 bg-navy-900">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-space text-3xl md:text-4xl font-bold text-white mb-6">
              Questions About Our Operations?
            </h2>
            <p className="text-xl text-platinum-light max-w-2xl mx-auto mb-10">
              We value transparency. If you have any questions about our role, processes, or compliance status, please contact us directly.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
              <Link to="/contact">
                <Button className="bg-gradient-to-r from-gold-light via-gold to-gold-dark text-navy-900 hover:shadow-xl hover:shadow-gold/25 px-10 py-6 text-lg font-semibold rounded-lg">
                  Contact Us
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/legal/terms-and-conditions">
                <Button variant="outline" className="border-2 border-platinum/30 text-white hover:bg-white/10 px-10 py-6 text-lg font-semibold rounded-lg">
                  View Terms & Conditions
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default TrustCompliancePage;
